import type { TableState } from "./types";
import type { EditorAction } from "../editor/reducers";
import type { TableEditorStep1Fragment } from "../client/generated";
import type { Dispatch } from "react";
import { useState } from "react";
import { mdiTableBorder } from "@mdi/js";
import { TableEditorStep } from "./TableEditorStep";
import { AccessButton } from "@acdc2/ui/components/access-button";
import { useTranslation } from "react-i18next";
import { predictTable } from "../client/predictions";
import { useAuth } from "react-oidc-context";

type Props = {
  fragment: TableEditorStep1Fragment;
  dispatch: Dispatch<EditorAction>;
  tableState: TableState;
};

export function TableEditorStep1({ dispatch, fragment, tableState }: Props) {
  const { t } = useTranslation();
  const auth = useAuth();
  const [loading, setLoading] = useState(false);

  const onPredict = async () => {
    if (!fragment.pngUrl || !auth.user) return;

    const { position, cols, rows } = tableState;
    const tableWidth = cols.reduce((sum, col) => sum + col.width, 0);
    const tableHeight = rows.reduce((sum, row) => sum + row.height, 0);

    try {
      setLoading(true);

      const prediction = await predictTable(
        fragment.pngUrl,
        auth.user.access_token,
        {
          top: position.y,
          left: position.x,
          width: tableWidth,
          height: tableHeight,
        },
      );

      dispatch({ type: "predictTable", prediction });
    } finally {
      setLoading(false);
    }
  };

  return (
    <TableEditorStep
      value="step1"
      iconPath={mdiTableBorder}
      title={t("TableEditorStep1.title")}
      description={t("TableEditorStep1.description")}
    >
      <AccessButton
        type="button"
        variant="outline"
        onClick={onPredict}
        loading={loading}
        confetti
      >
        {t("TableEditorStep1.inputs.predict.label")}
      </AccessButton>
    </TableEditorStep>
  );
}
